import { defineMessages } from '../define';

/**
 * Platform settings screen.
 *
 * Only the human label and help text beside each setting live here. The setting
 * keys themselves (`platform.timezone`, `smtp.host`, …) are the wire format the
 * gateway stores and must never be translated.
 */
export const settings = defineMessages({
  en: {
    'settings.title': 'Settings',
    'settings.description':
      'Platform-wide configuration. Changes apply within about thirty seconds across every process.',
    'settings.saved': 'Settings saved',
    'settings.requestFailed':
      'The request could not be completed. Check that the gateway is reachable.',

    'settings.forbidden.title': 'Administrators only',
    'settings.forbidden.description':
      'These settings change retention, security limits and mail delivery for everyone on this install, so they are limited to admins and the owner.',

    'settings.tab.general': 'General',
    'settings.tab.retention': 'Retention',
    'settings.tab.security': 'Security',
    'settings.tab.integrations': 'Integrations',

    'settings.general.description': 'How this install identifies itself.',
    'settings.platformName.label': 'Platform name',
    'settings.platformName.hint': 'Shown in the sidebar and in emails.',
    'settings.publicUrl.label': 'Public URL',
    'settings.publicUrl.hint':
      'The address people reach this console on. Used to build links in notifications.',
    'settings.timezone.label': 'Timezone',
    'settings.timezone.hint': 'An IANA name. Used for daily counters and retention cut-offs.',

    'settings.retention.description':
      'Deleting here means deleting from the database. There is no recycle bin and no export step — take a backup first if the data might be needed.',
    'settings.messageDays.label': 'Message retention (days)',
    'settings.messageDays.hint':
      'Messages older than this are permanently deleted, with their attachments. 0 keeps them forever.',
    'settings.rawHtmlDays.label': 'Raw HTML retention (days)',
    'settings.rawHtmlDays.hint':
      'The captured page fragment behind each message. Useful for debugging the parser, large on disk. 0 keeps it forever.',
    'settings.logDays.label': 'Log retention (days)',
    'settings.logDays.hint':
      'Console log lines older than this are removed. The engineering log on disk is unaffected. 0 keeps them forever.',
    'settings.attachmentSize.label': 'Attachment size cap (MB)',
    'settings.attachmentSize.hint': 'Files above this are recorded but not downloaded.',
    'settings.mirror.label': 'Mirror attachments locally',
    'settings.mirror.hint':
      'Copies images and files into local storage. Off means the console only keeps the reference, which stops working once LINE expires the original.',
    'settings.retention.warning':
      'A value of 0 means keep forever. On a busy install that grows without limit.',

    'settings.security.description': 'Limits applied to every request and session.',
    'settings.rateLimitMax.label': 'Rate limit (requests)',
    'settings.rateLimitMax.hint': 'Requests allowed per client within the window below.',
    'settings.rateLimitWindow.label': 'Rate limit window (seconds)',
    'settings.heartbeat.label': 'Session heartbeat timeout (seconds)',
    'settings.heartbeat.hint':
      'How long a session can go without a heartbeat from its browser before it is marked disconnected.',
    'settings.operatorKeys.label': 'Operators may create API keys',
    'settings.operatorKeys.hint':
      "Off restricts key creation to admins. A key inherits its creator's access, so this is worth turning off on a shared install.",

    'settings.smtp.description': 'Used for email notification rules and password-related mail.',
    'settings.testEmail.send': 'Send test email',
    'settings.testEmail.sent': 'Test email sent',
    'settings.testEmail.sentDetail':
      'If it does not arrive, check the logs for the SMTP response.',
    'settings.smtp.host.label': 'Host',
    'settings.smtp.host.hint': 'Defaults to Gmail.',
    'settings.smtp.port.label': 'Port',
    'settings.smtp.user.label': 'Username',
    'settings.smtp.user.gmailHint': 'The full address, including @gmail.com.',
    'settings.smtp.password.label': 'Password',
    'settings.smtp.password.stored':
      'A password is stored. Clear the field and type a new one to replace it.',
    'settings.smtp.password.gmailHint':
      'A Google App Password, not the account password — Google stopped accepting the latter over SMTP. Generate one at myaccount.google.com/apppasswords with 2-Step Verification on.',
    'settings.smtp.password.hint': 'Stored encrypted and never returned by the API.',
    'settings.smtp.from.label': 'From address',
    'settings.smtp.from.gmailHint':
      'Leave blank to send as the username. Gmail rewrites a From that is not the account or one of its verified aliases.',
    'settings.smtp.from.hint':
      'What recipients see in the From header. Blank sends as the username.',
    'settings.smtp.secure.label': 'Use TLS on connect',
    'settings.smtp.secure.hint':
      'On for port 465. Leave off for 587, which upgrades with STARTTLS.',

    'settings.dirty.one': '{count} unsaved change',
    'settings.dirty.other': '{count} unsaved changes',
    'settings.discard': 'Discard',
  },
  th: {
    'settings.title': 'ตั้งค่า',
    'settings.description':
      'การตั้งค่าสำหรับทั้งระบบ การเปลี่ยนแปลงจะมีผลกับทุกโปรเซสภายในประมาณสามสิบวินาที',
    'settings.saved': 'บันทึกการตั้งค่าแล้ว',
    'settings.requestFailed': 'ดำเนินการตามคำขอไม่สำเร็จ กรุณาตรวจสอบว่าเกตเวย์เข้าถึงได้',

    'settings.forbidden.title': 'เฉพาะผู้ดูแลระบบเท่านั้น',
    'settings.forbidden.description':
      'การตั้งค่าเหล่านี้มีผลต่อระยะเก็บข้อมูล ขีดจำกัดด้านความปลอดภัย และการส่งอีเมลของทุกคนในระบบนี้ จึงเปิดให้เฉพาะผู้ดูแลระบบและเจ้าของเท่านั้น',

    'settings.tab.general': 'ทั่วไป',
    'settings.tab.retention': 'ระยะเก็บข้อมูล',
    'settings.tab.security': 'ความปลอดภัย',
    'settings.tab.integrations': 'การเชื่อมต่อภายนอก',

    'settings.general.description': 'ระบบนี้แสดงตัวตนของตัวเองอย่างไร',
    'settings.platformName.label': 'ชื่อแพลตฟอร์ม',
    'settings.platformName.hint': 'แสดงในแถบเมนูด้านข้างและในอีเมล',
    'settings.publicUrl.label': 'URL สาธารณะ',
    'settings.publicUrl.hint': 'ที่อยู่ที่ผู้ใช้เข้าถึงคอนโซลนี้ ใช้สร้างลิงก์ในการแจ้งเตือน',
    'settings.timezone.label': 'เขตเวลา',
    'settings.timezone.hint':
      'ใช้ชื่อรูปแบบ IANA ใช้กับตัวนับรายวันและกำหนดเวลาลบข้อมูลตามอายุ',

    'settings.retention.description':
      'การลบที่นี่คือการลบออกจากฐานข้อมูลจริง ไม่มีถังขยะและไม่มีขั้นตอนส่งออกข้อมูล หากยังอาจต้องใช้ข้อมูลอยู่ ให้สำรองข้อมูลไว้ก่อน',
    'settings.messageDays.label': 'ระยะเก็บข้อความ (วัน)',
    'settings.messageDays.hint':
      'ข้อความที่เก่ากว่านี้จะถูกลบถาวรพร้อมไฟล์แนบ ใส่ 0 เพื่อเก็บไว้ตลอด',
    'settings.rawHtmlDays.label': 'ระยะเก็บ HTML ต้นฉบับ (วัน)',
    'settings.rawHtmlDays.hint':
      'ส่วนของหน้าเว็บที่บันทึกไว้เบื้องหลังแต่ละข้อความ มีประโยชน์ตอนตรวจสอบตัวแยกข้อมูล แต่กินพื้นที่ดิสก์มาก ใส่ 0 เพื่อเก็บไว้ตลอด',
    'settings.logDays.label': 'ระยะเก็บบันทึกการทำงาน (วัน)',
    'settings.logDays.hint':
      'บรรทัดล็อกในคอนโซลที่เก่ากว่านี้จะถูกลบ ไม่กระทบไฟล์ล็อกฝั่งระบบบนดิสก์ ใส่ 0 เพื่อเก็บไว้ตลอด',
    'settings.attachmentSize.label': 'ขนาดไฟล์แนบสูงสุด (MB)',
    'settings.attachmentSize.hint': 'ไฟล์ที่ใหญ่กว่านี้จะถูกบันทึกรายการไว้แต่ไม่ดาวน์โหลด',
    'settings.mirror.label': 'สำเนาไฟล์แนบเก็บไว้ในเครื่อง',
    'settings.mirror.hint':
      'คัดลอกรูปภาพและไฟล์มาเก็บในที่จัดเก็บของระบบ หากปิดไว้ คอนโซลจะเก็บเพียงลิงก์อ้างอิง ซึ่งจะใช้ไม่ได้เมื่อ LINE ลบไฟล์ต้นฉบับ',
    'settings.retention.warning':
      'ค่า 0 หมายถึงเก็บไว้ตลอด ในระบบที่ใช้งานหนัก ข้อมูลจะโตขึ้นเรื่อย ๆ โดยไม่มีขีดจำกัด',

    'settings.security.description': 'ขีดจำกัดที่ใช้กับทุกคำขอและทุกเซสชัน',
    'settings.rateLimitMax.label': 'จำกัดจำนวนคำขอ (ครั้ง)',
    'settings.rateLimitMax.hint': 'จำนวนคำขอที่อนุญาตต่อผู้เรียกหนึ่งรายภายในช่วงเวลาด้านล่าง',
    'settings.rateLimitWindow.label': 'ช่วงเวลาจำกัดคำขอ (วินาที)',
    'settings.heartbeat.label': 'หมดเวลาสัญญาณจากเซสชัน (วินาที)',
    'settings.heartbeat.hint':
      'เซสชันจะขาดสัญญาณจากเบราว์เซอร์ได้นานเท่าใด ก่อนถูกทำเครื่องหมายว่าขาดการเชื่อมต่อ',
    'settings.operatorKeys.label': 'ให้ผู้ปฏิบัติงานสร้าง API key ได้',
    'settings.operatorKeys.hint':
      'หากปิดไว้ จะให้เฉพาะผู้ดูแลระบบสร้างคีย์ได้ คีย์จะได้สิทธิ์เท่ากับผู้สร้าง จึงควรปิดไว้ในระบบที่ใช้งานร่วมกันหลายคน',

    'settings.smtp.description': 'ใช้สำหรับกฎการแจ้งเตือนทางอีเมลและอีเมลที่เกี่ยวกับรหัสผ่าน',
    'settings.testEmail.send': 'ส่งอีเมลทดสอบ',
    'settings.testEmail.sent': 'ส่งอีเมลทดสอบแล้ว',
    'settings.testEmail.sentDetail': 'หากอีเมลไม่เข้า ให้ดูล็อกเพื่อตรวจสอบการตอบกลับจาก SMTP',
    'settings.smtp.host.label': 'โฮสต์',
    'settings.smtp.host.hint': 'ค่าเริ่มต้นคือ Gmail',
    'settings.smtp.port.label': 'พอร์ต',
    'settings.smtp.user.label': 'ชื่อผู้ใช้',
    'settings.smtp.user.gmailHint': 'ใส่อีเมลแบบเต็ม รวม @gmail.com ด้วย',
    'settings.smtp.password.label': 'รหัสผ่าน',
    'settings.smtp.password.stored':
      'มีรหัสผ่านเก็บไว้แล้ว ล้างช่องนี้แล้วพิมพ์รหัสใหม่เพื่อแทนที่',
    'settings.smtp.password.gmailHint':
      'ต้องใช้ App Password ของ Google ไม่ใช่รหัสผ่านของบัญชี เพราะ Google ไม่รับรหัสผ่านบัญชีผ่าน SMTP แล้ว สร้างได้ที่ myaccount.google.com/apppasswords โดยต้องเปิดการยืนยันตัวตนสองขั้นตอนไว้',
    'settings.smtp.password.hint': 'เก็บแบบเข้ารหัสไว้ และ API จะไม่ส่งค่ากลับมา',
    'settings.smtp.from.label': 'ที่อยู่ผู้ส่ง',
    'settings.smtp.from.gmailHint':
      'เว้นว่างไว้เพื่อส่งในนามชื่อผู้ใช้ Gmail จะเปลี่ยนค่า From ให้เอง หากไม่ใช่บัญชีนั้นหรือชื่อแทนที่ยืนยันแล้ว',
    'settings.smtp.from.hint': 'ชื่อที่ผู้รับเห็นในช่อง From หากเว้นว่างจะส่งในนามชื่อผู้ใช้',
    'settings.smtp.secure.label': 'ใช้ TLS ตั้งแต่ตอนเชื่อมต่อ',
    'settings.smtp.secure.hint':
      'เปิดสำหรับพอร์ต 465 ส่วนพอร์ต 587 ให้ปิดไว้ เพราะจะอัปเกรดด้วย STARTTLS',

    'settings.dirty.one': 'มีการเปลี่ยนแปลง {count} รายการที่ยังไม่บันทึก',
    'settings.dirty.other': 'มีการเปลี่ยนแปลง {count} รายการที่ยังไม่บันทึก',
    'settings.discard': 'ละทิ้งการเปลี่ยนแปลง',
  },
});
